/**
 * /login — Google sign-in surface.
 *
 * Uses @react-oauth/google's `GoogleLogin` button (Google Identity
 * Services). On success the button hands us the Google `credential`
 * (an id_token JWT). We POST it to `/v1/auth/google/callback`; the
 * gateway exchanges it for a kos_session cookie and a tenant bundle,
 * then we navigate to the post-login destination.
 *
 * The `GoogleOAuthProvider` itself lives in the top-level `AppShell`
 * so the same client id is shared across every login surface.
 */

import { type CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { ArrowLeft, Brain, Database, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Brand } from "@/components/brand";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";

import { redirectIfAuthed } from "../lib/loaders";

interface GoogleCallbackResponse {
  kos_token: string;
  expires_in: number;
}

export async function loginLoader({ request }: { request: Request }): Promise<null> {
  return redirectIfAuthed(request);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromParam = params.get("from");
  const errorParam = params.get("error");
  // Allow external redirect URLs (for desktop app SSO callback)
  const from = fromParam?.startsWith("/") || fromParam?.startsWith("http://127.0.0.1") || fromParam?.startsWith("http://localhost")
    ? fromParam
    : "/chat";
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(errorParam);

  const handleSuccess = async (resp: CredentialResponse): Promise<void> => {
    const credential = resp.credential;
    if (!credential) {
      setError("Google did not return a credential.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await api<GoogleCallbackResponse>("/auth/google/callback", {
        method: "POST",
        body: JSON.stringify({ id_token: credential }),
      });

      // If `from` is an external URL (e.g. Lemon Agent desktop app callback),
      // redirect there with the token as a query parameter.
      if (from.startsWith("http://") || from.startsWith("https://")) {
        const url = new URL(from);
        url.searchParams.set("token", result.kos_token);
        window.location.href = url.toString();
        return;
      }

      navigate(from, { replace: true });
    } catch (err) {
      setPending(false);
      if (err instanceof ApiError) {
        setError(err.message || `HTTP ${err.status}`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const handleError = (): void => {
    setError(
      "Google sign-in failed. Make sure VITE_GOOGLE_OAUTH_CLIENT_ID is set and matches the gateway's KOS_GOOGLE_OAUTH_CLIENT_ID.",
    );
  };

  return (
    <div className="relative isolate flex min-h-screen flex-col bg-cream">
      <div
        aria-hidden
        className="grain-bg pointer-events-none absolute inset-x-0 top-0 h-[720px]"
      />
      <main className="relative z-10 flex flex-1 flex-col">
        {/* Top bar — back-link to marketing site */}
        <div className="mx-auto w-full max-w-6xl px-6 pt-8">
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            Back to home
          </Link>
        </div>

        <div className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-6 py-10 md:py-16">
          <div className="grid w-full gap-14 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-20">
            {/* ── Brand pane (desktop only) ───────────────────────────────── */}
            <div className="hidden lg:block">
              <Brand href={null} />

              <h1 className="mt-10 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-[44px]">
                Welcome back to your <span className="text-clay">team's</span> agent workspace.
              </h1>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                Bring your own LLMs, tools, and knowledge. We bring the runtime, the wiki, and the
                trace-driven feedback that makes your agents cheaper, faster, and more accurate
                every week.
              </p>

              <ul className="mt-10 grid max-w-md gap-5">
                <Feature
                  icon={<Brain className="h-4 w-4" />}
                  title="Production-grade agent runtime"
                  body="OTel traces, cost tracking, and A/B routing — built in."
                />
                <Feature
                  icon={<Database className="h-4 w-4" />}
                  title="Knowledge with citations"
                  body="Wiki, docs, and OCR pipelines pinned to versioned chunks."
                />
                <Feature
                  icon={<ShieldCheck className="h-4 w-4" />}
                  title="Multi-tenant by default"
                  body="org × team × user enforced everywhere — no quiet failures."
                />
              </ul>
            </div>

            {/* ── Sign-in card ────────────────────────────────────────────── */}
            <div className="mx-auto w-full max-w-[420px] lg:mx-0 lg:justify-self-end">
              <div className="mb-8 flex justify-center lg:hidden">
                <Brand href={null} />
              </div>

              <Card className="rounded-3xl border-border/60 bg-card/95 shadow-xl shadow-clay/10 backdrop-blur">
                <CardHeader className="space-y-2 px-7 pt-8">
                  <CardTitle className="font-serif text-3xl font-semibold tracking-tight">
                    Sign in
                  </CardTitle>
                  <CardDescription className="text-[15px] leading-relaxed">
                    Use your Google account to continue. First-time sign-in creates a personal
                    workspace.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 px-7 pb-8">
                  {error ? (
                    <div className="rounded-xl border border-destructive/20 bg-destructive/[0.04] px-4 py-3">
                      <p className="text-sm font-medium text-destructive">Sign-in failed</p>
                      <p className="mt-1 text-xs leading-relaxed text-destructive/80">{error}</p>
                    </div>
                  ) : null}

                  <div className="flex justify-center" aria-busy={pending ? "true" : "false"}>
                    <GoogleLogin
                      onSuccess={handleSuccess}
                      onError={handleError}
                      // useOneTap shows the small auto-prompt while the
                      // visible button stays available; both routes
                      // converge on `handleSuccess`.
                      useOneTap
                      theme="outline"
                      size="large"
                      text="continue_with"
                      shape="pill"
                    />
                  </div>

                  <div className="relative pt-1">
                    <div className="absolute inset-x-0 top-1/2 -z-10 h-px bg-border/70" />
                    <div className="flex justify-center">
                      <span className="bg-card px-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                        Single sign-on only
                      </span>
                    </div>
                  </div>

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Early-access internal build. Account provisioning, team management, and SSO
                    controls land alongside the backend identity service.
                  </p>
                </CardContent>
              </Card>

              <p className="mt-6 text-center text-xs text-muted-foreground">
                Need access?{" "}
                <a
                  className="font-medium text-foreground underline underline-offset-2 transition-colors hover:text-clay"
                  href="mailto:[email protected]"
                >
                  Talk to us
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-3.5">
      <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
        {icon}
      </span>
      <div className="space-y-0.5">
        <div className="text-sm font-medium leading-tight">{title}</div>
        <div className="text-xs leading-relaxed text-muted-foreground">{body}</div>
      </div>
    </li>
  );
}
